// CryptoRatesTable.tsx (client)
'use client';

import { format } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { Skeleton } from '../../../../components/ui/skeleton';
import { cn } from '../../lib/utils';

interface CryptoRate {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price_usd: number;
  price_change_24h_usd: number;
  price_change_percentage_24h: number;
  market_cap_usd: number;
  current_price_rub: number;
  price_change_24h_rub: number;
  lastUpdatedAtFromSource?: string; // ISO string
}

interface CryptoRatesCacheData {
  rates: CryptoRate[];
  lastUpdated: string | null; // ISO string
  source: string;
  message?: string;
}

const formatCurrency = (
  value: number | undefined | null,
  currency: 'USD' | 'RUB',
  compact = false,
) => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return value.toLocaleString(currency === 'USD' ? 'en-US' : 'ru-RU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: currency === 'RUB' ? 4 : 2,
    notation: compact ? 'compact' : undefined,
  });
};

const PriceChangePercent = memo(({ value }: { value: number }) => {
  if (isNaN(value)) return <span className="text-slate-400">N/A</span>;
  const colorClass = value > 0 ? 'text-green-400' : value < 0 ? 'text-red-400' : 'text-slate-400';
  return (
    <span className={cn(colorClass, 'flex items-center gap-1')}>
      {value > 0 && <FaCaretUp aria-hidden="true" />}
      {value < 0 && <FaCaretDown aria-hidden="true" />}
      {Math.abs(value).toFixed(2)}%
    </span>
  );
});
PriceChangePercent.displayName = 'PriceChangePercent';

const CryptoRateRow = memo(({ crypto }: { crypto: CryptoRate }) => {
  const [imgSrc, setImgSrc] = useState(crypto.image || '/crypto-placeholder.svg');

  // если картинка обновилась — сбросим
  useEffect(() => {
    setImgSrc(crypto.image || '/crypto-placeholder.svg');
  }, [crypto.image]);

  return (
    <tr className="border-b border-slate-700 hover:bg-slate-700/30 transition-colors group">
      <td className="px-3 py-4">
        <Link
          href={`/finance/crypto/${crypto.id}`}
          className="flex items-center gap-3"
          aria-label={`Подробнее о ${crypto.name} (${crypto.symbol})`}
          prefetch={false}
        >
          <Image
            src={imgSrc}
            alt=""
            width={32}
            height={32}
            sizes="32px"
            className="rounded-full bg-slate-600"
            unoptimized // ✅ важно для Docker/самохоста: не грузим image optimizer
            onError={() => setImgSrc('/crypto-placeholder.svg')}
          />
          <div>
            <span className="font-medium text-white group-hover:text-blue-400 transition-colors block truncate max-w-[120px] sm:max-w-none">
              {crypto.name}
            </span>
            <span className="block text-xs text-slate-400 uppercase">{crypto.symbol}</span>
          </div>
        </Link>
      </td>

      <td className="px-3 py-4 text-white tabular-nums">
        {formatCurrency(crypto.current_price_usd, 'USD')}
      </td>

      <td className={cn('px-3 py-4 tabular-nums', 'hidden md:table-cell')}>
        <PriceChangePercent value={crypto.price_change_percentage_24h} />
      </td>

      <td className="px-3 py-4 text-white tabular-nums">
        {formatCurrency(crypto.current_price_rub, 'RUB')}
      </td>

      <td className={cn('px-3 py-4 text-slate-400 tabular-nums', 'hidden md:table-cell')}>
        {formatCurrency(crypto.market_cap_usd, 'USD', true)}
      </td>
    </tr>
  );
});
CryptoRateRow.displayName = 'CryptoRateRow';

export function CryptoRatesTable({ initialData }: { initialData?: CryptoRatesCacheData }) {
  const [cryptoData, setCryptoData] = useState<CryptoRatesCacheData>(
    initialData ?? { rates: [], lastUpdated: null, source: 'initial' },
  );
  const [loading, setLoading] = useState<boolean>(!initialData);
  const [error, setError] = useState<string | null>(null);

  const fetchCryptoRates = useCallback(async () => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/finance/crypto-rates', {
        signal: controller.signal,
        cache: 'no-store', // пусть CDN решает, а клиент всегда берёт актуальный endpoint
      });
      if (!res.ok) throw new Error('Не удалось загрузить данные о криптовалютах.');
      const data: CryptoRatesCacheData = await res.json();
      setCryptoData(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('Ошибка при загрузке данных:', err);
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных.');
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, []);

  // ✅ если initialData пришли — вообще не дёргаем API на маунте
  useEffect(() => {
    if (!initialData) fetchCryptoRates();
  }, [initialData, fetchCryptoRates]);

  const lastUpdatedText = useMemo(() => {
    if (!cryptoData.lastUpdated) return null;
    try {
      return format(new Date(cryptoData.lastUpdated), 'dd.MM.yyyy HH:mm:ss');
    } catch {
      return null;
    }
  }, [cryptoData.lastUpdated]);

  if (loading) {
    return (
      <div
        className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg p-4 md:p-6"
        aria-busy="true"
      >
        <Skeleton className="w-1/2 h-8 mb-2" />
        <Skeleton className="w-3/4 h-5 mb-4" />
        <Skeleton className="w-1/3 h-4 mb-6" />
        <div className="space-y-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-4 py-2 border-b border-slate-700 last:border-b-0"
            >
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-5 flex-grow" />
              <Skeleton className="h-5 w-1/5 hidden md:block" />
              <Skeleton className="h-5 w-1/5" />
              <Skeleton className="h-5 w-1/5 hidden md:block" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 text-center text-red-400 min-h-[300px] flex flex-col items-center justify-center"
        role="alert"
      >
        <h2 className="text-xl font-bold mb-2">Ошибка загрузки</h2>
        <p>{error}</p>
        <button
          onClick={fetchCryptoRates}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <article className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg overflow-hidden">
      <div className="p-4 md:p-6">
        <header>
          <h2 className="text-2xl font-bold text-main mb-1">Курсы криптовалют</h2>
          <p className="text-slate-300 text-sm md:text-base mb-4">
            Актуальные курсы криптовалют за последние 24 часа.
          </p>
        </header>

        {lastUpdatedText && (
          <p
            className={cn(
              'text-xs md:text-sm mb-4',
              cryptoData.source === 'database_fallback' && 'text-yellow-400',
              cryptoData.source === 'unavailable' && 'text-red-400',
              (!cryptoData.source || ['coincap', 'coingecko'].includes(cryptoData.source)) &&
                'text-slate-400',
            )}
          >
            Обновлено: {lastUpdatedText} | Источник:{' '}
            {cryptoData.source === 'coincap'
              ? 'CoinCap'
              : cryptoData.source === 'coingecko'
                ? 'CoinGecko'
                : cryptoData.source === 'database_fallback'
                  ? 'База данных (резерв)'
                  : 'Нет данных'}
            {cryptoData.message && <span className="ml-2 font-medium">{cryptoData.message}</span>}
          </p>
        )}

        {cryptoData.rates.length === 0 ? (
          <div className="text-center text-slate-400 py-10 min-h-[200px] flex flex-col items-center justify-center">
            <p>Данные о криптовалютах временно отсутствуют.</p>
            <p className="text-sm mt-2">Попробуйте обновить страницу позже.</p>
          </div>
        ) : (
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full min-w-[500px] md:min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="px-3 py-3 text-left text-slate-400 font-medium uppercase text-xs tracking-wider w-[180px] sm:w-auto">
                    Валюта
                  </th>
                  <th className="px-3 py-3 text-left text-slate-400 font-medium uppercase text-xs tracking-wider">
                    Цена USD
                  </th>
                  <th className="px-3 py-3 text-left text-slate-400 font-medium uppercase text-xs tracking-wider hidden md:table-cell">
                    Изм. 24ч (%)
                  </th>
                  <th className="px-3 py-3 text-left text-slate-400 font-medium uppercase text-xs tracking-wider">
                    Цена RUB
                  </th>
                  <th className="px-3 py-3 text-left text-slate-400 font-medium uppercase text-xs tracking-wider hidden md:table-cell">
                    Капитализация (USD)
                  </th>
                </tr>
              </thead>
              <tbody>
                {cryptoData.rates.map((crypto) => (
                  <CryptoRateRow key={crypto.id} crypto={crypto} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </article>
  );
}
